import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Button,
  Space,
  Input,
  Row,
  Col,
  Popconfirm,
  message,
  Tag,
  Image,
  Tooltip,
  Switch,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { categoryService } from '../../services';
import { formatDate, debounce } from '../../utils/helpers';

const CategoryList = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();

  useEffect(() => {
    loadCategories();
  }, []);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 📱 Mobile Category Card Component
  const MobileCategoryCard = ({ category }) => {
    const categoryId = category.category_id ?? category.id ?? category.categoryId;
    
    return (
      <Card
        size="small"
        style={{
          marginBottom: 12,
          border: '1px solid #d9d9d9',
          borderRadius: 8,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
        bodyStyle={{ padding: 12 }}
      >
        <div>
          <div style={{ 
            fontWeight: 'bold', 
            fontSize: 14, 
            marginBottom: 4,
            color: '#333'
          }}>
            {category.name}
          </div>
          
          {category.parent_name && (
            <div style={{ 
              fontSize: 12, 
              color: '#666',
              marginBottom: 6 
            }}>
              <Tag size="small" color="green">
                Danh mục con của: {category.parent_name}
              </Tag>
            </div>
          )}

          {category.description && (
            <div style={{ 
              fontSize: 12, 
              color: '#666',
              marginBottom: 8,
              lineHeight: 1.4
            }}>
              {category.description.length > 80 
                ? `${category.description.substring(0, 80)}...` 
                : category.description
              }
            </div>
          )}

          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 8
          }}>
            <Space size={4}>
              <Button
                type="primary"
                size="small"
                icon={<EditOutlined />}
                onClick={() => {
                  if (categoryId) navigate(`/categories/edit/${categoryId}`);
                  else message.error('Không xác định được ID phân loại');
                }}
              >
                Sửa
              </Button>
              <Popconfirm
                title="Xóa phân loại?"
                description="Sẽ ảnh hưởng đến các sản phẩm thuộc phân loại này."
                onConfirm={() => {
                  if (categoryId) handleDelete(categoryId);
                  else message.error('Không xác định được ID phân loại');
                }}
                okText="Có"
                cancelText="Không"
                placement="topRight"
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                />
              </Popconfirm>
            </Space>
          </div>
        </div>
      </Card>
    );
  };

  const loadCategories = async () => {
    setLoading(true);
    try {
      const response = await categoryService.getAll();
      if (response.success) {
        // Normalize id field: backend may return `id` or `category_id`
        const list = (response.data || []).map(c => ({
          ...c,
          category_id: c.category_id ?? c.id ?? c.categoryId ?? null,
        }));
        setCategories(list);
      }
    } catch (error) {
      //console.error('Error loading categories:', error);
      message.error('Không thể tải danh sách phân loại');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (categoryId) => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      message.error('Bạn chưa đăng nhập hoặc token không hợp lệ. Vui lòng đăng nhập lại.');
      return;
    }
    try {
      const response = await categoryService.delete(categoryId);
      if (response.success) {
        message.success('Xóa phân loại thành công');
        loadCategories();
      } else {
        message.error(response.message || 'Xóa phân loại thất bại');
      }
    } catch (error) {
      //console.error('Error deleting category:', error);
      message.error('Xóa phân loại thất bại');
    }
  };

  const handleSearch = debounce((value) => {
    setSearchText(value);
  }, 500);

  // Lọc danh mục theo từ khóa tìm kiếm
  const filteredCategories = categories.filter(category =>
    category.name?.toLowerCase().includes(searchText.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchText.toLowerCase())
  );

  const columns = [
    {
      title: 'Tên phân loại',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => {
        const cid = record.category_id ?? record.id ?? record.categoryId ?? 'N/A';
        return (
          <div>
            <div style={{ fontWeight: 'bold' }}>{text}</div>
            <div style={{ fontSize: '12px', color: '#888' }}>
              ID: {cid}
            </div>
          </div>
        );
      },
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
      render: (text) => (
        <div style={{ maxWidth: 300 }}>
          {text && text.length > 100 ? `${text.substring(0, 100)}...` : text || 'Không có mô tả'}
        </div>
      ),
    },
    {
      title: 'Phân loại cha',
      dataIndex: 'parent_name',
      key: 'parent_name',
      render: (text) => text || <em style={{ color: '#888' }}>Phân loại gốc</em>,
    },
    // product_count and trạng thái removed: only id, name, description, parent_id shown
  // display_order column removed per DB schema
  // created_at column removed per request
    {
      title: 'Thao tác',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Chỉnh sửa">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
                const cid = record.category_id ?? record.id ?? record.categoryId;
                if (cid) navigate(`/categories/edit/${cid}`);
                else message.error('Không xác định được ID phân loại');
              }}
            />
          </Tooltip>
          <Tooltip title="Xóa">
            <Popconfirm
              title="Bạn có chắc chắn muốn xóa phân loại này?"
              description="Hành động này sẽ ảnh hưởng đến các sản phẩm thuộc phân loại này."
              onConfirm={() => {
                const cid = record.category_id ?? record.id ?? record.categoryId;
                if (cid) handleDelete(cid);
                else message.error('Không xác định được ID phân loại');
              }}
              okText="Có"
              cancelText="Không"
            >
              <Button icon={<DeleteOutlined />} size="small" danger />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  return (
    <div style={{ padding: 24 }}>
      <Card>
        {/* Bộ lọc và tìm kiếm */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="Tìm kiếm phân loại..."
              prefix={<SearchOutlined />}
              onChange={(e) => handleSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={16} style={{ textAlign: 'right' }}>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadCategories}
                loading={loading}
              />
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/categories/add')}
              >
                Thêm phân loại
              </Button>
            </Space>
          </Col>
        </Row>

        {/* Thông tin tổng quan (giữ tổng phân loại) */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8}>
            <div style={{ 
              background: '#f0f9ff', 
              padding: 16, 
              borderRadius: 8,
              textAlign: 'center' 
            }}>
              <div style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                {categories.length}
              </div>
              <div style={{ color: '#666' }}>Tổng phân loại</div>
            </div>
          </Col>
        </Row>

        {/* Responsive Category Display */}
        {isMobile ? (
          // Mobile: Card Layout
          <div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: 16,
              padding: '8px 0',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <div style={{ fontSize: 14, color: '#666' }}>
                Hiển thị {filteredCategories.length} phân loại
              </div>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={loadCategories}
                loading={loading}
              >
                Tải lại
              </Button>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: 40 }}>
                <div>Đang tải...</div>
              </div>
            ) : filteredCategories.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: 40,
                color: '#999'
              }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>📂</div>
                <div>Không có phân loại nào</div>
              </div>
            ) : (
              filteredCategories.map(category => (
                <MobileCategoryCard 
                  key={category.category_id ?? category.id ?? category.categoryId} 
                  category={category} 
                />
              ))
            )}
          </div>
        ) : (
          // Desktop: Table Layout
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={filteredCategories}
            rowKey={(record) => record.category_id ?? record.id ?? record.categoryId}
            loading={loading}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} của ${total} phân loại`,
            }}
            scroll={{ x: 1000 }}
          />
        )}

        {/* Hành động hàng loạt */}
        {selectedRowKeys.length > 0 && (
          <div style={{ 
            marginTop: 16, 
            padding: 16, 
            background: '#f8f9fa', 
            borderRadius: 8 
          }}>
            <Space>
              <span>Đã chọn {selectedRowKeys.length} phân loại</span>
              <Button size="small">Xóa hàng loạt</Button>
              <Button size="small">Kích hoạt hàng loạt</Button>
              <Button size="small">Tạm dừng hàng loạt</Button>
            </Space>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CategoryList;
